import api from './api';
import { handleApiError } from './apiError';
import { Booking } from './bookingsApi';

/**
 * ── participationRequestsApi — applying to an approval-gated ticket tier ──
 *
 * Two calls, mirroring the pair [[bookingsApi]] exposes for its own flow:
 *
 *   `createParticipationRequest`  POST /events/{id}/participation-request
 *   `getParticipationRequest`     GET  /me/participation-requests/{id}
 *
 * Applying doesn't hold a seat and doesn't create a booking — it only records
 * the ask for an organizer to review on Backstage. There is no client
 * endpoint to approve/reject/block; see `bruno/ozone/rsvp`'s own docs for the
 * full contract this was built against, including the `GET /events/{id}`
 * side (read there — [[eventsApi]]'s `requiresGuestApproval` /
 * `requiresSocialLink` / `request_pending_approval`).
 *
 * Casing: see [[api]] — bodies here are written camelCase.
 */

export type ParticipationRequestStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'blocked';

/**
 * One application, read as the app renders it.
 *
 * The wire's `approval_queries` is a generic `{key: answer}` map — the tier's
 * own `approval_queries` on `GET /events/{id}` says which keys are required,
 * and this is the answers. This app only ever asks the one question, so it's
 * projected straight onto `socialLink` here rather than kept as a dict — the
 * same collapse [[eventsApi]]'s `requiresSocialLink` does on the way in.
 *
 * No event or tier name on this row, on purpose — the wire doesn't send one
 * (see the rsvp collection's own docs on this). A caller that needs "your
 * request to *this event*" has to have kept the event id itself from when it
 * applied.
 */
export type ParticipationRequest = {
  id: string;
  status: ParticipationRequestStatus;
  approvalQueries: {
    socialLink: string | null;
  };
  /** Set once an organizer approves — the booking that got created for it. */
  booking: Booking;
  createdAt: string;
};

/**
 * Apply to a `requiresGuestApproval` tier. 🔒 Authenticated — the "request to
 * attend" button. Refuses a user whose profile isn't complete, same as
 * `createBooking`.
 *
 * Everything else throws an [[ApiError]] whose message is meant to be shown:
 * the tier isn't gated (400), a required answer is missing (400), a prior
 * `blocked` verdict (403), or the tier's per-user application cap (409).
 */
async function createParticipationRequest(input: {
  eventId: string;
  slotTierId: string;
  /** Omitted when the tier's `approvalQueries` ask nothing. */
  socialLink?: string;
}): Promise<ParticipationRequest> {
  const response = await api.post<ParticipationRequest>(
    `/participation-requests`,
    {
      event: input.eventId,
      slotTier: input.slotTierId,
      approvalQueries: input.socialLink ? { socialLink: input.socialLink } : {},
    }
  );

  if (!response.ok || !response.data) handleApiError(response);

  return response.data;
}

/**
 * Read one application back, by id. 🔒 Authenticated, and scoped to the
 * caller — someone else's id, or an unknown one, is a 404, never a 403.
 *
 * This is `/requests/[id]`'s whole fetch: both TicketTier's "View Request"
 * action and confirm's post-submit redirect land here.
 */
async function getParticipationRequest(
  id: string
): Promise<ParticipationRequest> {
  const response = await api.get<ParticipationRequest>(
    `/me/participation-requests/${id}`
  );

  if (!response.ok || !response.data) handleApiError(response);

  return response.data;
}

/**
 * Every application the caller has ever made, newest first. 🔒 Authenticated,
 * scoped to the caller by the token alone. Same cursor envelope as
 * `listBookings` in [[bookingsApi]] — only the first page is read; see that
 * function's note on why paging is skipped for now.
 */
async function listParticipationRequests(): Promise<ParticipationRequest[]> {
  const response = await api.get<ParticipationRequestsBody>(
    '/me/participation-requests'
  );

  if (!response.ok || !response.data) handleApiError(response);

  const { data, page } = response.data;

  if (page?.hasMore) {
    // TODO: add pagination here
  }

  return data.filter((r) => r.booking);
}

/** The cursor envelope `/me/participation-requests` wraps its page in. */
type ParticipationRequestsBody = {
  data: ParticipationRequest[];
  page?: {
    cursor: string | null;
    nextCursor: string | null;
    hasMore: boolean;
  };
};

/** The wire shape, already camelCased by [[api]] — see `fromWire`. */
export default {
  createParticipationRequest,
  getParticipationRequest,
  listParticipationRequests,
};
